// src/hooks/useProjects.js
import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'

// ── helpers: camelCase ↔ snake_case ────────────────────────────────────────
function toDb(p) {
  return {
    user_id:               p.userId,
    name:                  p.name,
    chain:                 p.chain,
    category:              p.category,
    status:                p.status,
    priority:              p.priority,
    estimated_reward:      Number(p.estimatedReward) || 0,
    invested_amount:       Number(p.investedAmount)  || 0,
    wallet_used:           p.walletUsed   || null,
    start_date:            p.startDate    || null,
    deadline:              p.deadline     || null,
    website:               p.website      || null,
    twitter:               p.twitter      || null,
    discord:               p.discord      || null,
    notes:                 p.notes        || null,
    tags:                  p.tags         || null,
    funding_amount:        p.fundingAmount|| null,
    backers:               p.backers      || null,
    requires_daily_checkin: p.requiresDailyCheckin || false,
    checkin_actions:       p.checkinActions || [],
    tasks:                 p.tasks        || [],
  }
}

function fromDb(row) {
  return {
    id:                    row.id,
    userId:                row.user_id,
    name:                  row.name,
    chain:                 row.chain,
    category:              row.category,
    status:                row.status,
    priority:              row.priority,
    estimatedReward:       row.estimated_reward ?? '',
    investedAmount:        row.invested_amount  ?? '',
    walletUsed:            row.wallet_used  || '',
    startDate:             row.start_date   || '',
    deadline:              row.deadline     || '',
    website:               row.website      || '',
    twitter:               row.twitter      || '',
    discord:               row.discord      || '',
    notes:                 row.notes        || '',
    tags:                  row.tags         || '',
    fundingAmount:         row.funding_amount || '',
    backers:               row.backers      || '',
    requiresDailyCheckin:  row.requires_daily_checkin || false,
    checkinActions:        row.checkin_actions || [],
    tasks:                 row.tasks        || [],
    createdAt:             row.created_at,
    updatedAt:             row.updated_at,
  }
}

function checkinFromDb(row) {
  return {
    id:        row.id,
    projectId: row.project_id,
    date:      row.date,
    time:      row.time || '',
    actions:   row.actions || [],
    note:      row.note || '',
    createdAt: row.created_at,
  }
}

// ── hook ───────────────────────────────────────────────────────────────────
export function useProjects(userId) {
  const [projects,  setProjects]  = useState([])
  const [checkins,  setCheckins]  = useState({}) // { projectId: [...] }
  const [loading,   setLoading]   = useState(true)
  const [syncing,   setSyncing]   = useState(false)
  const [error,     setError]     = useState(null)

  // ── FETCH ALL ─────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!userId) return
    setLoading(true)
    setError(null)
    try {
      const [{ data: pRows, error: pErr }, { data: cRows, error: cErr }] = await Promise.all([
        supabase.from('projects').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
        supabase.from('checkins').select('*').eq('user_id', userId).order('date',       { ascending: false }),
      ])
      if (pErr) throw pErr
      if (cErr) throw cErr

      setProjects(pRows.map(fromDb))

      // Group checkins by project_id
      const grouped = {}
      for (const row of cRows) {
        const pid = row.project_id
        if (!grouped[pid]) grouped[pid] = []
        grouped[pid].push(checkinFromDb(row))
      }
      setCheckins(grouped)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => { fetchAll() }, [fetchAll])

  // ── SAVE PROJECT (insert or update) ───────────────────────────────────
  const saveProject = async (project) => {
    setSyncing(true)
    setError(null)
    try {
      const payload = toDb({ ...project, userId })

      if (project.id) {
        // UPDATE
        const { data, error } = await supabase
          .from('projects')
          .update(payload)
          .eq('id', project.id)
          .eq('user_id', userId)
          .select()
          .single()
        if (error) throw error
        setProjects(ps => ps.map(p => p.id === project.id ? fromDb(data) : p))
        return fromDb(data)
      } else {
        // INSERT
        const { data, error } = await supabase
          .from('projects')
          .insert(payload)
          .select()
          .single()
        if (error) throw error
        const newProj = fromDb(data)
        setProjects(ps => [newProj, ...ps])
        return newProj
      }
    } catch (e) {
      setError(e.message)
      throw e
    } finally {
      setSyncing(false)
    }
  }

  // ── DELETE PROJECT ────────────────────────────────────────────────────
  const deleteProject = async (id) => {
    setSyncing(true)
    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id)
        .eq('user_id', userId)
      if (error) throw error
      setProjects(ps => ps.filter(p => p.id !== id))
      setCheckins(c => { const n = { ...c }; delete n[id]; return n })
    } catch (e) {
      setError(e.message)
      throw e
    } finally {
      setSyncing(false)
    }
  }

  // ── ADD CHECKIN ───────────────────────────────────────────────────────
  const addCheckin = async (projectId, entry) => {
    setSyncing(true)
    try {
      const { data, error } = await supabase
        .from('checkins')
        .insert({
          project_id: projectId,
          user_id:    userId,
          date:       entry.date,
          time:       entry.time,
          actions:    entry.actions,
          note:       entry.note || null,
        })
        .select()
        .single()
      if (error) throw error
      const ci = checkinFromDb(data)
      setCheckins(c => ({ ...c, [projectId]: [ci, ...(c[projectId] || [])] }))
      return ci
    } catch (e) {
      setError(e.message)
      throw e
    } finally {
      setSyncing(false)
    }
  }

  return { projects, checkins, loading, syncing, error, saveProject, deleteProject, addCheckin, refetch: fetchAll }
}
